import { EditOutlined } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  FormGroup,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import React, { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useMutation } from 'react-query'
import {
  AnimalDocumentDtoAttributes,
  AnimalDocumentDto,
  AnimalDocumentDtoType,
  UploadAnimalDocumentDtoAttributes,
} from '../../../../interactors/gen/backendClient'
import { backendClient } from '../../../../interactors/clients/client'
import { useAnimalStore } from '../../../../store/AnimalStore'
import { useGlobalSnackbarStore } from '../../../../store/GlobalSnackBarStore'
import { useDialogOpened } from '../../../../utils/hooks/useDialogOpened'
import { isValidPastDateCoherence } from '../../../../utils/date/isValidPastDateCoherence'
import { ControlledDateField } from '../../../common/ControlledDateField'
import { DisplayChipLikeTextField } from '../../../common/DisplayChipLikeTextField'
import { getReadableAnimalDocumentType } from '../../../../domain/Animal/AnimalDisplay'
import { blueSky, colors, spacingItem } from '../../../theme'

interface EditAnimalDocumentFormValues {
  name?: string
  reference: string
  establishmentDate?: string
  requiredForRegisterEntry: boolean
  requiredForRegisterExit: boolean
}

interface Props {
  animalId: string
  document: AnimalDocumentDto
  setDocumentsList?: React.Dispatch<React.SetStateAction<AnimalDocumentDto[]>>
  disabled?: boolean
}

export const EditDocumentModal: React.FC<Props> = ({ animalId, document, setDocumentsList, disabled = false }) => {
  const { isDialogOpen, openDialog, closeDialog } = useDialogOpened()
  const globalSnackbar = useGlobalSnackbarStore()
  const animalStore = useAnimalStore()
  const isVeterinaryCertificate = document.type === AnimalDocumentDtoType.VeterinaryCertificate
  const isOtherDocument = document.type === AnimalDocumentDtoType.Others
  const documentAttributes = Array.isArray(document.attributes) ? document.attributes : []

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<EditAnimalDocumentFormValues>({
    defaultValues: {
      name: document.name || '',
      reference: document.reference || '',
      establishmentDate: document.establishmentDate || undefined,
      requiredForRegisterEntry: documentAttributes.includes(AnimalDocumentDtoAttributes.Entry),
      requiredForRegisterExit: documentAttributes.includes(AnimalDocumentDtoAttributes.Exit),
    },
    mode: 'onChange',
  })

  useEffect(() => {
    reset({
      name: document.name || '',
      reference: document.reference || '',
      establishmentDate: document.establishmentDate || undefined,
      requiredForRegisterEntry: documentAttributes.includes(AnimalDocumentDtoAttributes.Entry),
      requiredForRegisterExit: documentAttributes.includes(AnimalDocumentDtoAttributes.Exit),
    })
  }, [document, documentAttributes, reset])

  const mutation = useMutation(
    async (data: EditAnimalDocumentFormValues) => {
      const attributes = []
      if (data.requiredForRegisterEntry) {
        attributes.push(UploadAnimalDocumentDtoAttributes.Entry)
      }
      if (data.requiredForRegisterExit) {
        attributes.push(UploadAnimalDocumentDtoAttributes.Exit)
      }

      await backendClient.put(`animals/${animalId}/documents/${document.id}`, {
        reference: data.reference,
        name: isOtherDocument ? data.name?.trim() || undefined : undefined,
        establishmentDate: isVeterinaryCertificate ? data.establishmentDate || undefined : undefined,
        attributes,
      })
    },
    {
      onSuccess: (_response, data) => {
        closeDialog()
        globalSnackbar.triggerSuccessMessage(`Le document a bien été modifié !`)
        setDocumentsList?.((prev) =>
          prev.map((currentDocument) =>
            currentDocument.id === document.id
              ? {
                  ...currentDocument,
                  reference: data.reference,
                  name: isOtherDocument ? data.name?.trim() || undefined : currentDocument.name,
                  establishmentDate: isVeterinaryCertificate
                    ? data.establishmentDate || undefined
                    : currentDocument.establishmentDate,
                  attributes: [
                    ...(data.requiredForRegisterEntry ? [AnimalDocumentDtoAttributes.Entry] : []),
                    ...(data.requiredForRegisterExit ? [AnimalDocumentDtoAttributes.Exit] : []),
                  ],
                }
              : currentDocument
          )
        )

        if (animalStore.selectedAnimal?.id === animalId) {
          void animalStore.refreshSelectedAnimalInBackground()
        }
      },
    }
  )

  const onSubmit = (data: EditAnimalDocumentFormValues) => {
    mutation.mutate(data)
  }

  return (
    <>
      <Tooltip title={disabled ? 'Vous ne pouvez pas modifier ce document.' : 'Modifier le document'} placement="top">
        <span>
          <IconButton
            size="small"
            disabled={disabled}
            onClick={openDialog}
            sx={{
              width: { xs: 28, sm: 32 },
              height: { xs: 28, sm: 32 },
              borderRadius: 2,
              color: '#D39A12',
              backgroundColor: 'rgba(243, 172, 0, 0.08)',
              '&:hover': { backgroundColor: 'rgba(243, 172, 0, 0.16)' },
            }}
          >
            <EditOutlined sx={{ fontSize: { xs: 17, sm: 19 } }} />
          </IconButton>
        </span>
      </Tooltip>

      <Dialog
        open={isDialogOpen}
        onClose={closeDialog}
        PaperProps={{
          style: { borderRadius: 20, padding: 0, border: '1px solid #E4E8F2' },
        }}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent sx={{ p: 3 }}>
            <Typography
              variant="h4"
              component="h2"
              sx={{ marginBottom: 3, fontSize: '1.08rem', fontWeight: 800, color: colors.cello }}
            >
              Modifier le document
            </Typography>

            <Box sx={{ mb: 2 }}>
              <DisplayChipLikeTextField
                label=""
                value={
                  isOtherDocument ? document.name || 'Autre document' : getReadableAnimalDocumentType(document.type)
                }
                fullWidth
                documentPage
                color={blueSky}
                compact
                borderless
                emphasis
              />
            </Box>

            {isOtherDocument && (
              <TextField
                label="Nom du document"
                type="text"
                fullWidth
                size="small"
                required
                {...register('name', { required: 'Le nom est requis' })}
                error={!!errors.name}
                helperText={errors.name?.message}
                sx={spacingItem}
              />
            )}

            {isVeterinaryCertificate && (
              <ControlledDateField
                control={control}
                validate={(value) => isValidPastDateCoherence(value) || !value}
                fieldName={'establishmentDate'}
                label="Date de l'établissement du certificat"
                error={errors.establishmentDate}
                requiredRule={"La date de l'établissement est requise"}
                size="small"
                sx={spacingItem}
              />
            )}

            <TextField
              label="Libellé"
              {...register('reference')}
              error={!!errors.reference}
              helperText={errors.reference?.message}
              type="text"
              fullWidth
              size="small"
              sx={spacingItem}
            />

            <FormGroup>
              <Controller
                control={control}
                name="requiredForRegisterEntry"
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!field.value}
                        onChange={(_event, checked) => field.onChange(checked)}
                        inputRef={field.ref}
                      />
                    }
                    label="Justificatif nécessaire au registre d'entrée"
                    sx={spacingItem}
                  />
                )}
              />
              <Controller
                control={control}
                name="requiredForRegisterExit"
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!field.value}
                        onChange={(_event, checked) => field.onChange(checked)}
                        inputRef={field.ref}
                      />
                    }
                    label="Justificatif nécessaire au registre de sortie"
                  />
                )}
              />
            </FormGroup>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, pt: 0 }}>
            <Button
              data-testid="cancel"
              onClick={closeDialog}
              sx={{ textTransform: 'none', borderRadius: '999px', px: 2.25, fontWeight: 600 }}
            >
              Annuler
            </Button>
            <LoadingButton
              data-testid="apply-changes"
              variant="contained"
              type="submit"
              loading={mutation.isLoading}
              sx={{ borderRadius: '999px', minHeight: 38, px: 2.4, textTransform: 'none' }}
            >
              Enregistrer
            </LoadingButton>
          </DialogActions>
        </form>
      </Dialog>
    </>
  )
}
